import { Analytics } from '@cobliteam/analytics';
import { ConditionCategory } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/conditions';
import { RuleCategory, RuleMode } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import { Location } from '@cobliteam/common-types';
import { useUser } from '@cobliteam/hooks';
import { useCreateTrigger, useGetRules } from '@cobliteam/hooks/src/useEntity/useAlerts';
import Button from '@cobliteam/ui/components/Button';
import Dialog from '@cobliteam/ui/components/Dialog';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import queryString from 'query-string';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useTriggersStore from '../../../../store/useTriggersStore';
import { mapFormValuesToTriggerRecord } from '../../../../types/triggerFormValues';
import EditTemplateBanner from './components/EditTemplateBanner/EditTemplateBanner';
import TriggerTemplateSummary from './components/TriggerTemplateSummary';
import { createTriggerTemplate } from './utils';

const TriggerTemplateModal = () => {
  const template = useTriggersStore((state) => state.activeTemplate!);
  const setTemplate = useTriggersStore((state) => state.setActiveTemplate);
  const openTemplateModal = useTriggersStore((state) => state.openTemplateModal);
  const isTriggerCreated = useTriggersStore((state) => state.isTriggerCreated);

  const { getUser } = useUser();
  const { name } = getUser();
  const history = useHistory();

  const { data: rules, isLoading: isFetchingRules, isSuccess: isRulesLoaded } = useGetRules(RuleMode.FLEET_POLICY);
  const { mutateAsync: createMutate, isPending: isCreatingTrigger, isSuccess } = useCreateTrigger();

  const title = {
    road: 'Modelo de excesso de velocidade da via',
    speed: 'Modelo de excesso de velocidade do veículo',
    idle: 'Modelo de motor ocioso',
  }[template];

  const condition = {
    speed: (
      <>
        <b>exceder o limite de velocidade cadastrado</b> por mais de <b>um minuto</b>
      </>
    ),
    road: (
      <>
        <b>exceder 51% do limite de velocidade da via</b> por mais de <b>um minuto</b>
      </>
    ),
    idle: (
      <>
        <b>exceder o tempo de três minutos de motor ocioso</b>
      </>
    ),
  }[template];

  const handleEditTemplate = () => {
    const templateRule = {
      road: RuleCategory.ROAD_SPEED_LIMIT,
      speed: RuleCategory.VEHICLE_SPEED_LIMIT,
      idle: RuleCategory.IDLE_ENGINE,
    }[template];

    const templateCondition = {
      road: ConditionCategory.ROAD_SPEED_LIMIT_1_MINUTE,
      speed: ConditionCategory.VEHICLE_SPEED_LIMIT_1_MINUTE,
      idle: ConditionCategory.IDLE_ENGINE_3_MINUTES,
    }[template];

    const params = queryString.stringify({
      templateRule: templateRule,
      templateCondition: templateCondition,
    });
    Analytics.track('Edit Template Clicked');
    history.push(`${Location.ALERTS_TRIGGERS_CREATE}?${params}`);
  };

  const handleCreateTemplate = () => {
    try {
      if (!isRulesLoaded) return;

      Analytics.track('Save Template Clicked');

      const triggerTemplate = createTriggerTemplate(template, rules, getUser());
      const triggerToCreate = mapFormValuesToTriggerRecord(triggerTemplate);

      return createMutate(triggerToCreate);
    } catch (e) {
      return e;
    }
  };

  const handleOnClose = () => {
    setTemplate(undefined);
    isTriggerCreated(false);
  };

  useEffect(() => {
    if (!isCreatingTrigger && isSuccess) {
      isTriggerCreated(true);
      setTemplate(undefined);
    }
  }, [isCreatingTrigger, isSuccess, isTriggerCreated, setTemplate]);

  return (
    <Dialog open={openTemplateModal}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description className='sr-only' />
          <Dialog.Close onClick={handleOnClose} />
        </Dialog.Header>

        <Dialog.Body className='mt-5 gap-6'>
          <div className='flex flex-col gap-4'>
            <div className='flex items-center gap-1'>
              <Icon name='list-checks' color='primary' />
              <Typography variant='description-title' className='text-gray-black'>
                Resumo da regra
              </Typography>
            </div>
            <Typography variant='body' className='text-gray-dark'>
              Quando um veículo {condition}, me notifique.
            </Typography>
          </div>
          <TriggerTemplateSummary name={name} template={template} />

          <EditTemplateBanner onClick={handleEditTemplate} />
        </Dialog.Body>

        <Dialog.Footer className='flex justify-end gap-4'>
          <Button variant='ghost' onClick={handleOnClose}>
            Cancelar
          </Button>

          <Button disabled={!isRulesLoaded || isCreatingTrigger} onClick={handleCreateTemplate} loading={isCreatingTrigger || isFetchingRules}>
            Salvar regra
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};

export default TriggerTemplateModal;
